import {
  PROJECT_WORKFLOW_STATUS_LABELS,
  type ProjectWorkflowStatusKey,
  type SOrdnerOut,
} from "@/lib/projects";
import type { ProjectFilters } from "@/pages/projekte/useProjectFilters";

export type ProjectFilterHeadingOptions = {
  /** Volle Zeile für gewählten Ordner: „Nummer – Bezeichnung“ (ohne „Ordner:“-Präfix). */
  ordnerLine?: string | null;
  /** Volle Zeile für gewählten S-Ordner: „Nummer – Ort · Ort-Zusatz – Bezeichnung“. */
  sordnerLine?: string | null;
};

/** „Nummer – Bezeichnung“ für S-Ordner-Überschriften. */
export function buildSOrdnerHeadingLine(hit: Pick<SOrdnerOut, "nummer" | "beschreibung">): string {
  const nummer = (hit.nummer || "").trim();
  const desc = (hit.beschreibung || "").trim();
  return desc ? `${nummer} – ${desc}` : nummer;
}

/** Seitenüberschrift: „Nummer – Ort · Ort-Zusatz – Bezeichnung“. */
export function buildSOrdnerFilterHeading(
  hit: Pick<SOrdnerOut, "nummer" | "beschreibung" | "ort_name" | "ort_name_text"> & {
    hero_address_title?: string | null;
  },
): string {
  const nummer = (hit.nummer || "").trim();
  const location = formatSOrdnerLocation(hit);
  const desc = (hit.beschreibung || "").trim();
  return [nummer, location, desc].filter(Boolean).join(" – ");
}

/** „Adresse #123“ / „Ort #123“ sind keine echten Ortsnamen. */
const PLACEHOLDER_PLACE_LABEL = /^(?:adresse|ort) #\d+$/i;

export function usablePlaceLabel(value: string | null | undefined): string | null {
  const text = (value || "").trim();
  if (!text || PLACEHOLDER_PLACE_LABEL.test(text)) return null;
  return text;
}

/** Anzeigename einer Hero-Adresse: Titel, sonst Straße und Ort. */
export function heroAddressPlaceLabel(addr: {
  title?: string | null;
  street?: string | null;
  zipcode?: string | null;
  city?: string | null;
}): string | null {
  const title = usablePlaceLabel(addr.title);
  if (title) return title;
  const street = (addr.street || "").trim();
  const cityLine = [(addr.zipcode || "").trim(), (addr.city || "").trim()].filter(Boolean).join(" ");
  const line = [street, cityLine].filter(Boolean).join(", ");
  return line || null;
}

/** Ort (Hero-Adresse / orte) plus hinterlegter Namenszusatz. */
export function formatSOrdnerLocation(
  hit: Pick<SOrdnerOut, "ort_name" | "ort_name_text"> & { hero_address_title?: string | null },
): string | null {
  const ort = usablePlaceLabel(hit.hero_address_title) || usablePlaceLabel(hit.ort_name);
  const zusatz = (hit.ort_name_text || "").trim();
  if (ort && zusatz) return `${ort} · ${zusatz}`;
  return ort || zusatz || null;
}

/**
 * Entspricht der Logik um `selectedOrdnerHeading` in `auftraege.js` (filterAndRender):
 * Priorität Ordner → Kunde → Jahr / „Alle Projekte“, separater S‑Ordner‑Pfad.
 */
export function buildProjectFilterHeading(
  f: ProjectFilters,
  options?: ProjectFilterHeadingOptions,
): string {
  if (f.jahr === "S") {
    if (f.sordner.trim()) {
      const line = options?.sordnerLine?.trim();
      if (line) return line;
      return f.sordner.trim();
    }
    return "S-Ordner – alle Jahre";
  }

  if (f.ordner.trim()) {
    const line = options?.ordnerLine?.trim();
    if (line) return line;
    return f.ordner.trim();
  }

  if (f.kunde.trim()) {
    return `Kunde: ${f.kunde.trim()}`;
  }

  if (f.jahr === null) {
    return "Alle Projekte";
  }

  if (typeof f.jahr === "number") {
    return `Projekte ${f.jahr}`;
  }

  return "Alle Projekte";
}

/** Kurzer Zusatz zu Status / Suche (optional unter der Überschrift). Der S-Ordner-Ort steht in der Überschrift. */
export function buildProjectFilterContextExtras(f: ProjectFilters): string | null {
  const bits: string[] = [];
  if (f.status.length) {
    const labels = f.status.map((status) =>
      status === "abgeschlossen"
        ? "Abgeschlossen"
        : PROJECT_WORKFLOW_STATUS_LABELS[status as ProjectWorkflowStatusKey] || status,
    );
    bits.push(`Status: ${labels.join(", ")}`);
  }
  if (f.q.trim()) bits.push(`Suche: „${f.q.trim()}“`);
  if (!bits.length) return null;
  return bits.join(" · ");
}
